import {
  CoreBatchAction,
  type ID,
  resolveSymbol,
  selectTabNodes,
} from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { AIActions } from '@store/reducers/ai'
import { artBoardActions } from '@store/reducers/artboard'
import { SeoActions } from '@store/reducers/seo/actions'
import {
  commitProjectRequest,
  rollbackToBackupSnapshot,
  saveBackupSnapshot,
} from '@storeOld/actionCreators'
import { useDispatch, useSelector } from 'react-redux'

import { DEFAULT_PROJECT_NAME } from '@/constants/constants'
import { VITE_AI_HOST } from '@/constants/env'
import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import {
  clearAiNotifications,
  notifyAiGeneratingFinished,
  notifyAiInProgress,
} from '@/hooks/ai/actionCreators'
import useIndexer, { type Indexer } from '@/hooks/useIndexer'
import type { AppState } from '@/types'
import { parseCsv, postEventStreamV2 } from '@/utils/streamsV2'
import { buildSeoAction, type SeoAction } from './buildSeoAction'

const selectNodeBlockNames = (
  node: ID,
  { relations, manager: { project }, library }: AppState
) => {
  const children = relations.dependencies[node]?.children || []

  const blockIds = children.filter(
    (id) => relations.dependencies[id]?.type === 'block'
  )

  const symbolNames = project.symbols.reduce(
    (accum, current) => {
      accum[current.id] =
        resolveSymbol(current, library.symbol, project.colors)?.title ?? ''
      return accum
    },
    {} as Record<ID, string>
  )

  const names = blockIds
    .map((id) => project.blocks[id])
    .map((b) => {
      if (b.type === 'monolithic') {
        return b.title
      }

      return symbolNames[b.symbolId]
    })

  return names.map((name) => ({ name }))
}

const useTakePages = (tabId: ID, indexer: Indexer<string>) => {
  const state = useSelector((state: AppState) => state)

  const nodes = selectTabNodes(tabId)({
    project: state.manager.project,
    relations: state.relations,
  })

  return nodes.map(({ id, title }) => ({
    index: indexer.addAndGetIndex(id),
    name: title,
    blocks: selectNodeBlockNames(id, state),
  }))
}

export const useGenerateSeoTags = () => {
  const dispatch = useDispatch()
  const indexer = useIndexer<string>()

  const projectTitle = useSelector(migrationProjectSelector.getProjectTitle)

  const activeTabId = useSelector(
    migrationProjectSelector.getActiveOrFirstTabId
  )

  const pages = useTakePages(activeTabId, indexer)

  return async () => {
    let abortController: (() => void) | null = null

    const cleanup = () => {
      if (abortController) {
        window.removeEventListener('CancelAiGeneration', handleCancel)
        abortController = null
      }
    }

    const handleCancel = () => {
      dispatch(clearAiNotifications())

      if (abortController) {
        abortController()
      }
    }

    sendGtmEvent(GtmTriggerName.AI_PROJECT_SEO)

    // Up front, not on success: the tags land on the tree as they arrive, and
    // only SEO mode renders them.
    dispatch(SeoActions.enableMode())

    const projectName =
      projectTitle === DEFAULT_PROJECT_NAME || !projectTitle
        ? undefined
        : projectTitle

    const [stream, getAbortController] = postEventStreamV2(
      `${VITE_AI_HOST}/v2/generate-seo-tags`,
      {
        projectName,
        pages,
      }
    )

    abortController = getAbortController

    window.addEventListener('CancelAiGeneration', handleCancel)

    dispatch(artBoardActions.setAiGeneratingStatus({ status: true }))
    dispatch(AIActions.setGenerating(true))

    // Rows are applied one by one now, so cancelling has something to undo.
    dispatch(saveBackupSnapshot())

    dispatch(
      notifyAiInProgress(() => {
        getAbortController()
        dispatch(rollbackToBackupSnapshot())
        dispatch(clearAiNotifications())
        dispatch(artBoardActions.setAiGeneratingStatus({ status: false }))
        dispatch(AIActions.setGenerating(false))
      }, 'Cancel')
    )

    const actionsBatch: SeoAction[] = []

    parseCsv(stream).on('row', ({ value }) => {
      const [id, metaTitle, metaDescription, metaSlug] = value

      const action = buildSeoAction(
        id,
        { title: metaTitle, description: metaDescription, slug: metaSlug },
        indexer
      )

      if (!action) {
        return
      }

      dispatch(action)
      actionsBatch.push(action)
    })

    stream.on('abort', () => {
      cleanup()
      dispatch(rollbackToBackupSnapshot())
      dispatch(clearAiNotifications())
      dispatch(artBoardActions.setAiGeneratingStatus({ status: false }))
      dispatch(AIActions.setGenerating(false))
    })

    stream.on('error', () => {
      dispatch(rollbackToBackupSnapshot())
    })

    stream.on('attemptsWasted', () => {
      dispatch(clearAiNotifications())
      dispatch(artBoardActions.setAiGeneratingStatus({ status: false }))
      dispatch(AIActions.setGenerating(false))
    })

    stream.on('success', () => {
      cleanup()
      dispatch(
        commitProjectRequest(
          CoreBatchAction({
            type: 'ai_generate_seo_tags',
            actions: actionsBatch,
          })
        )
      )

      dispatch(clearAiNotifications())
      dispatch(notifyAiGeneratingFinished())
      setTimeout(() => {
        dispatch(clearAiNotifications())
      }, 8000)
      dispatch(artBoardActions.setAiGeneratingStatus({ status: false }))
      dispatch(AIActions.setGenerating(false))

      dispatch(
        AIActions.addMessage({
          message: {
            type: 'assistant',
            text: 'All pages now include SEO tags based on the project’s structure and content.',
          },
        })
      )
    })
  }
}
